/**
 * FinalCtaSection — the closing call-to-action.
 *
 * A LIGHT section (`bg-background`) holding one big bordered brutalist PANEL — an
 * OUTLINED GRID, NOT a drop-shadowed card. The panel is a 3-column grid: two
 * decorative icon columns (left + right, hidden below `lg`) sit FLUSH against the
 * panel's outer border, separated from the center by full-height vertical dividers,
 * each column an evenly-divided stack of large brand-colored icons. They flank a
 * centered headline + a SINGLE "SCHEDULE A DEMO" button.
 *
 * The button is the dominant element: it carries the layered `shadow-brand` (Teal +
 * Cardinal + black, stacked) for the "super highlighted" look and OPENS the
 * DemoRequestDialog (tagged 'demo'). It is the only CTA.
 *
 * A neobrutalist ART piece peeks up from the panel's bottom edge (concentric brand
 * bands + a star burst), clipped by the panel's `overflow-hidden`, sitting BEHIND the
 * content (z-0). It replaces the old four thin arcs.
 *
 * Rendered inside the `.retroui-root` ancestor (brand tokens + Preflight-OFF), so
 * EVERY border / divider is written explicitly with `border-solid` / `divide-solid` —
 * without Preflight the default border-style is `none`, which would make the hard
 * outlines invisible. ZERO MUI.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  HeartPulse,
  Activity,
  Watch,
  Database,
  ShieldCheck,
  Sparkles,
  LineChart,
  Smartphone,
} from 'lucide-react';
import { useContactDialog } from './ContactDialogProvider';

/** A single decorative icon cell. Cells `flex-1` to split the column's full height
 *  evenly (a clean full-height grid); horizontal dividers come from the parent's
 *  `divide-y-2`, so the cell itself carries no border. */
const IconCell: React.FC<{
  icon: React.ReactNode;
  /** Brand color utility for the icon (e.g. "text-primary"). */
  colorClass: string;
}> = ({ icon, colorClass }) => (
  <div className="flex flex-1 items-center justify-center px-3" aria-hidden="true">
    <span className={colorClass}>{icon}</span>
  </div>
);

/** Large icon sizing — bigger than before so the marks nearly fill each cell. */
const ICON = 'h-9 w-9 lg:h-10 lg:w-10';

const FinalCtaSection: React.FC = () => {
  // The shared contact dialog (owned by ContactDialogProvider). The "SCHEDULE A DEMO"
  // button opens it tagged as the 'demo' source.
  const { open } = useContactDialog();

  // Respect reduced-motion: when set, render in the final (settled) state so a
  // static screenshot shows the panel fully visible (no mid-animation capture).
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="bg-background px-4 pb-20 pt-6 md:px-8 md:pb-28 md:pt-12">
      <motion.div
        // Subtle fade-up on scroll-in; gated on reduced-motion (final state when reduced).
        initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        // OUTLINED grid panel — 2px black border, square, NO drop shadow, NO inner padding
        // (the icon columns sit flush to the border).
        className="relative mx-auto max-w-[1240px] overflow-hidden border-2 border-solid border-black bg-card"
      >
        {/* ===== Neobrutalist art — peeks up from the bottom edge, BEHIND content. ===== */}
        {/* "Rainbow Bands + Sunburst Sticker": four chunky concentric brand-color rings
            (yellow → green → teal → cardinal) rise from the bottom center, crested by a
            layered 12-point cardinal sunburst sticker (white star → yellow core → black
            eye) with two sticker studs pressed onto the outer ring. All shapes are
            black-outlined, centered at x=220, mirror-symmetric. The `viewBox` keeps ~14px
            of headroom above the outer ring so its top stroke is never clipped. */}
        <div
          className="pointer-events-none absolute inset-x-0 -bottom-px z-0 flex justify-center"
          aria-hidden="true"
        >
          <svg
            className="h-[186px] w-[440px]"
            viewBox="0 -6 440 186"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Concentric filled semicircle bands (outer → inner): yellow, green, teal, cardinal. */}
            <path
              d="M 50 180 A 170 170 0 0 1 390 180 L 348 180 A 128 128 0 0 0 92 180 Z"
              fill="#F5C518"
              stroke="#000000"
              strokeWidth="4"
              strokeLinejoin="round"
            />
            <path
              d="M 92 180 A 128 128 0 0 1 348 180 L 310 180 A 90 90 0 0 0 130 180 Z"
              fill="#1FA85C"
              stroke="#000000"
              strokeWidth="4"
              strokeLinejoin="round"
            />
            <path
              d="M 130 180 A 90 90 0 0 1 310 180 L 274 180 A 54 54 0 0 0 166 180 Z"
              fill="#00505C"
              stroke="#000000"
              strokeWidth="4"
              strokeLinejoin="round"
            />
            <path
              d="M 166 180 A 54 54 0 0 1 274 180 L 242 180 A 22 22 0 0 0 198 180 Z"
              fill="#8C1515"
              stroke="#000000"
              strokeWidth="4"
              strokeLinejoin="round"
            />

            {/* Flanking sticker studs pressed onto the outer (yellow) ring crest, symmetric about x=220. */}
            <circle cx="136" cy="32" r="8" fill="#8C1515" stroke="#000000" strokeWidth="4" />
            <circle cx="304" cy="32" r="8" fill="#00505C" stroke="#000000" strokeWidth="4" />

            {/* Chunky 12-point sunburst sticker (the hero), symmetric about x=220. */}
            {/* Cardinal outer star with a hard black outline. */}
            <path
              d="M 220 18 L 212 36 L 196 24 L 198 44 L 178 42 L 190 58 L 172 66 L 190 74 L 178 90 L 198 88 L 196 108 L 212 96 L 220 114 L 228 96 L 244 108 L 242 88 L 262 90 L 250 74 L 268 66 L 250 58 L 262 42 L 242 44 L 244 24 L 228 36 Z"
              fill="#8C1515"
              stroke="#000000"
              strokeWidth="5"
              strokeLinejoin="round"
            />
            {/* White inner star for the layered poster look. */}
            <path
              d="M 220 32 L 214 45 L 203 37 L 204 50 L 191 49 L 199 60 L 186 66 L 199 72 L 191 83 L 204 82 L 203 95 L 214 87 L 220 100 L 226 87 L 237 95 L 236 82 L 249 83 L 241 72 L 254 66 L 241 60 L 249 49 L 236 50 L 237 37 L 226 45 Z"
              fill="#FFFFFF"
              stroke="#000000"
              strokeWidth="4"
              strokeLinejoin="round"
            />
            {/* Yellow core sticker with a bold black center dot. */}
            <circle cx="220" cy="66" r="17" fill="#F5C518" stroke="#000000" strokeWidth="4" />
            <circle cx="220" cy="66" r="6" fill="#000000" />
          </svg>
        </div>

        {/* ===== 3-column outlined grid: icons | center | icons ===== */}
        <div className="relative z-10 flex min-h-[480px] items-stretch md:min-h-[540px]">
          {/* LEFT icon column — flush to the panel's left border; full-height divider on the right. */}
          <div className="hidden w-24 shrink-0 flex-col divide-y-2 divide-solid divide-black self-stretch border-r-2 border-solid border-black lg:flex lg:w-28">
            <IconCell icon={<HeartPulse className={ICON} />} colorClass="text-primary" />
            <IconCell icon={<Activity className={ICON} />} colorClass="text-secondary" />
            <IconCell icon={<Watch className={ICON} />} colorClass="text-green" />
            <IconCell icon={<Database className={ICON} />} colorClass="text-yellow" />
          </div>

          {/* CENTER — headline + the single button. */}
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center md:px-10 md:py-20">
            <h2 className="font-head text-3xl uppercase leading-tight md:text-5xl">
              <span className="block text-foreground">Ready to run</span>
              <span className="block text-primary">A better study?</span>
            </h2>

            <div className="mt-8 flex justify-center">
              {/* The ONE CTA — "super highlighted" layered `shadow-brand` (Teal + Cardinal
                  + black). Hover nudges down+right and shrinks the shadow for a press
                  effect; active resets flat. Opens the demo dialog. */}
              <button
                type="button"
                onClick={() => open('demo')}
                className="border-2 border-solid border-black bg-card px-8 py-3 font-sans font-bold uppercase tracking-[0.15em] text-foreground shadow-brand transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-md active:translate-x-0 active:translate-y-0 active:shadow-brand"
              >
                Schedule a demo
              </button>
            </div>

            {/* Reserve vertical room so the bottom art clears the button. */}
            <div className="h-24 md:h-32" aria-hidden="true" />
          </div>

          {/* RIGHT icon column — flush to the panel's right border; full-height divider on the left. */}
          <div className="hidden w-24 shrink-0 flex-col divide-y-2 divide-solid divide-black self-stretch border-l-2 border-solid border-black lg:flex lg:w-28">
            <IconCell icon={<ShieldCheck className={ICON} />} colorClass="text-secondary" />
            <IconCell icon={<Sparkles className={ICON} />} colorClass="text-yellow" />
            <IconCell icon={<LineChart className={ICON} />} colorClass="text-primary" />
            <IconCell icon={<Smartphone className={ICON} />} colorClass="text-green" />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default FinalCtaSection;
